import { AgentNetProduction } from '@fluentsms/agentnet-web-icons';
import {
  Alert,
  AlertTitle,
  Button,
  Card,
  makeStyles,
  Paper,
  TextField,
  Theme,
  Typography,
} from '@fluentsms/components';
import { useTranslation } from '@fluentsms/i18n';
import resolveEnv from '@fluentsms/utils';
import React, { ReactElement, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

export type UnhandledExceptionProps = {
  resetErrorState?: () => void;
  error?: Error;
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    margin: '-8px',
  },
  alert: {
    flexDirection: 'column',
  },
  button: {
    backgroundColor: '#1C94B9',

    '&:hover, &:focus': {
      backgroundColor: '#11596f',
    },
  },
  card: {
    alignItems: 'center',
    backgroundColor: 'rgba(33, 52, 71, 0.35)',
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'center',
    overflowY: 'scroll',
    padding: '16px 8px',
    width: '100vw',
    [theme.breakpoints.up('md')]: {
      minHeight: '100vh',
    },
  },
  logo: {
    margin: '0 auto',
  },
  paper: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: '44px',
    maxWidth: '480px',
    padding: theme.spacing(4),
    textAlign: 'center',
    width: '100%',
  },
}));

export const UnhandledException = ({
  resetErrorState = () => undefined,
  error,
}: UnhandledExceptionProps): ReactElement | null => {
  const { t } = useTranslation('error');
  const classes = useStyles();
  const isNotProd = useMemo(() => resolveEnv() !== 'PROD', []);
  const history = useHistory();

  const navigateToLogin = (): void => {
    localStorage.clear();
    resetErrorState();
    history.push('/login');
  };

  if (error?.message.includes('Invalid token')) {
    navigateToLogin();
    return null;
  }

  return (
    <div className={classes.root} data-testid="unhandled-exception-page">
      <Card className={classes.card}>
        <Paper variant="outlined" className={classes.paper}>
          <AgentNetProduction className={classes.logo} fill="#003d66" width={225} />

          <Typography variant="h5">{t('unhandledException.heading')}</Typography>
          <Typography variant="body1">{t('unhandledException.body1')}</Typography>
          <Typography variant="body1">{t('unhandledException.body2')}</Typography>
          <Button
            className={classes.button}
            color="primary"
            data-testid="unhandled-exception-page-button"
            variant="contained"
            onClick={navigateToLogin}
          >
            {t('unhandledException.button')}
          </Button>

          {isNotProd && error && (
            <Alert className={classes.alert} severity="error">
              <AlertTitle>{t('unhandledException.error.heading')}</AlertTitle>
              <Typography gutterBottom>
                {t('unhandledException.error.location')} {window?.location?.href}
              </Typography>
              <TextField multiline rowsMax={6} variant="outlined" fullWidth label="Stack Trace" value={error?.stack} />
            </Alert>
          )}
        </Paper>
      </Card>
    </div>
  );
};
